import type { SuggesterStation } from './types'

/**
 * Maximum edit distance tolerated by the fuzzy fallback. Mirrors the iOS
 * `SuggesterManager` constant (`distance <= 6`).
 */
const FUZZY_MAX_DISTANCE = 6

/**
 * Maximum number of fuzzy fallback matches surfaced. Mirrors the iOS
 * `SuggesterManager.prefix(3)`.
 */
const FUZZY_MAX_RESULTS = 3

/**
 * Normalise a string for matching: lowercase, strip diacritics, drop any
 * non-alphanumeric character, then collapse runs of the same character.
 *
 * Byte-for-byte mirror of the iOS `SuggesterManager.normalize` so the
 * three platforms surface identical match sets for the same query.
 */
export function normalize(value: string): string {
  const folded = value
    .toLowerCase()
    .normalize('NFD')
    // Strip combining diacritical marks (U+0300–U+036F).
    .replace(/[̀-ͯ]/g, '')
  const alphanumericOnly = folded.replace(/[^a-z0-9]/g, '')
  let collapsed = ''
  let previous: string | undefined
  for (const ch of alphanumericOnly) {
    if (ch !== previous) collapsed += ch
    previous = ch
  }
  return collapsed
}

/**
 * Levenshtein edit distance between two strings. Iterative two-row
 * dynamic-programming implementation. Used only for the fuzzy fallback.
 */
export function levenshtein(a: string, b: string): number {
  const m = a.length
  const n = b.length
  if (m === 0) return n
  if (n === 0) return m
  let prev = Array.from({ length: n + 1 }, (_, j) => j)
  let curr = new Array<number>(n + 1)
  for (let i = 1; i <= m; i += 1) {
    curr[0] = i
    for (let j = 1; j <= n; j += 1) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1
      curr[j] = Math.min(prev[j] + 1, curr[j - 1] + 1, prev[j - 1] + cost)
    }
    ;[prev, curr] = [curr, prev]
  }
  return prev[n]
}

/**
 * Search `stations` for `query`.
 *
 * Two-stage, mirroring the iOS `SuggesterManager.search`:
 *   1. Substring match on the normalised name — input order preserved.
 *   2. If stage 1 is empty, the closest {@link FUZZY_MAX_RESULTS}
 *      stations within {@link FUZZY_MAX_DISTANCE} edit distance.
 *
 * An empty / whitespace-only query yields an empty array (the caller
 * renders the idle list instead).
 */
export function searchStations(
  query: string,
  stations: readonly SuggesterStation[],
): readonly SuggesterStation[] {
  const normalizedQuery = normalize(query)
  if (normalizedQuery.length === 0) return []

  const substringHits = stations.filter((s) => normalize(s.name).includes(normalizedQuery))
  if (substringHits.length > 0) return substringHits

  return stations
    .map((s) => ({ station: s, distance: levenshtein(normalize(s.name), normalizedQuery) }))
    .filter((scored) => scored.distance <= FUZZY_MAX_DISTANCE)
    .sort((a, b) => a.distance - b.distance)
    .slice(0, FUZZY_MAX_RESULTS)
    .map((scored) => scored.station)
}
